(() => {
  if (window.IVRViceProductExperienceLoaded) return;

  window.IVRViceProductExperienceLoaded = true;

  const EXPERIENCE_SELECTOR =
    "[data-vice-product-experience]";

  const COMBO_SELECTORS = [
    "#ancora-horizon-ofertas",
    "[data-ivr-vu-combo]",
    ".ivr-vu-combo"
  ];

  function getComboTarget() {
    for (const selector of COMBO_SELECTORS) {
      const target =
        document.querySelector(selector);

      if (target) {
        return target;
      }
    }

    return null;
  }

  function getHeaderOffset() {
    const header =
      document.querySelector(
        ".section-header"
      ) ||
      document.querySelector(
        "header"
      );

    if (!header) {
      return 18;
    }

    const style =
      window.getComputedStyle(
        header
      );

    const isFixed = [
      "fixed",
      "sticky"
    ].includes(
      style.position
    );

    return isFixed
      ? Math.min(
          header
            .getBoundingClientRect()
            .height + 18,
          150
        )
      : 18;
  }

  function smoothScrollTo(target) {
    if (!target) {
      return;
    }

    const reduceMotion =
      window
        .matchMedia(
          "(prefers-reduced-motion: reduce)"
        )
        .matches;

    const top =
      target
        .getBoundingClientRect()
        .top +
      window.scrollY -
      getHeaderOffset();

    window.scrollTo({
      top: Math.max(
        0,
        top
      ),

      behavior:
        reduceMotion
          ? "auto"
          : "smooth"
    });
  }

  function highlightCombo(target) {
    const combo =
      target.matches?.(
        "[data-ivr-vu-combo], .ivr-vu-combo"
      )
        ? target
        : target.querySelector?.(
            "[data-ivr-vu-combo], .ivr-vu-combo"
          );

    if (!combo) {
      return;
    }

    combo.classList.remove(
      "vice-combo-highlight"
    );

    void combo.offsetWidth;

    combo.classList.add(
      "vice-combo-highlight"
    );

    window.setTimeout(
      () => {
        combo.classList.remove(
          "vice-combo-highlight"
        );
      },
      1300
    );
  }

  function handleComboClick(event) {
    const link =
      event.target.closest(
        "[data-vice-scroll-combo]"
      );

    if (!link) {
      return;
    }

    const target =
      getComboTarget();

    if (!target) {
      return;
    }

    event.preventDefault();

    smoothScrollTo(
      target
    );

    window.setTimeout(
      () => {
        highlightCombo(
          target
        );
      },
      450
    );
  }

  function handleReviewClick(event) {
    const link =
      event.target.closest(
        "[data-vice-scroll-reviews]"
      );

    if (!link) {
      return;
    }

    const target =
      document.querySelector(
        "#vice-reviews-bottom"
      ) ||
      document.querySelector(
        ".jdgm-review-widget"
      );

    if (!target) {
      return;
    }

    event.preventDefault();

    smoothScrollTo(
      target
    );
  }

  function initReveal(root) {
    const items =
      Array.from(
        root.querySelectorAll(
          "[data-vice-reveal]"
        )
      );

    if (!items.length) {
      return;
    }

    if (
      !(
        "IntersectionObserver" in
        window
      ) ||
      window
        .matchMedia(
          "(prefers-reduced-motion: reduce)"
        )
        .matches
    ) {
      items.forEach(
        (item) => {
          item.classList.add(
            "is-visible"
          );
        }
      );

      return;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          entries.forEach(
            (entry) => {
              if (
                !entry.isIntersecting
              ) {
                return;
              }

              entry.target
                .classList
                .add(
                  "is-visible"
                );

              observer.unobserve(
                entry.target
              );
            }
          );
        },
        {
          rootMargin:
            "0px 0px -8% 0px",

          threshold:
            0.08
        }
      );

    items.forEach(
      (item, index) => {
        item.style
          .transitionDelay =
          `${Math.min(
            index % 4,
            3
          ) * 70}ms`;

        observer.observe(
          item
        );
      }
    );
  }

  function initMobileBuy(root) {
    const button =
      root.querySelector(
        "[data-vice-mobile-buy]"
      );

    if (!button) {
      return;
    }

    const combo =
      getComboTarget();

    const reviews =
      document.querySelector(
        "#vice-reviews-bottom"
      );

    function update() {
      const comboBottom =
        combo
          ? combo
              .getBoundingClientRect()
              .bottom +
            window.scrollY
          : 0;

      const reviewsTop =
        reviews
          ? reviews
              .getBoundingClientRect()
              .top +
            window.scrollY
          : document
              .documentElement
              .scrollHeight;

      const current =
        window.scrollY +
        window.innerHeight;

      const passedCombo =
        window.scrollY >
        comboBottom - 80;

      const beforeReviews =
        current <
        reviewsTop + 120;

      button.classList.toggle(
        "is-visible",
        passedCombo &&
          beforeReviews
      );
    }

    let ticking = false;

    function requestUpdate() {
      if (ticking) {
        return;
      }

      ticking = true;

      window.requestAnimationFrame(
        () => {
          update();

          ticking = false;
        }
      );
    }

    update();

    window.addEventListener(
      "scroll",
      requestUpdate,
      {
        passive: true
      }
    );

    window.addEventListener(
      "resize",
      requestUpdate
    );
  }

  function initAccordion(root) {
    const group = root.querySelector('.vice-product-experience__accordions');
    if (!group || group.dataset.viceAccordionReady === 'true') return;

    const panels = Array.from(group.children).filter((el) =>
      el.matches('details.vice-product-experience__accordion')
    );
    if (!panels.length) return;
    group.dataset.viceAccordionReady = 'true';

    const animations = new WeakMap();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let foundOpen = false;

    panels.forEach((panel) => {
      if (panel.open) {
        if (foundOpen) panel.open = false;
        else foundOpen = true;
      }
    });

    function transition(panel, opening) {
      const summary = panel.querySelector('summary');
      const content = panel.querySelector('.vice-product-experience__accordion-content');
      if (!summary || !content) {
        panel.open = opening;
        return;
      }

      const previous = animations.get(panel);
      const currentHeight = panel.getBoundingClientRect().height;
      const computed = window.getComputedStyle(content);
      const currentOpacity = panel.open ? Number.parseFloat(computed.opacity) || 0 : 0;
      const currentTransform = previous ? computed.transform : 'none';

      if (previous) {
        previous.height.cancel();
        previous.fade.cancel();
        animations.delete(panel);
      }

      if (reducedMotion.matches || typeof panel.animate !== 'function' || typeof content.animate !== 'function') {
        panel.open = opening;
        panel.style.removeProperty('overflow');
        return;
      }

      panel.open = true;
      const summaryHeight = summary.getBoundingClientRect().height;
      const contentHeight = content.getBoundingClientRect().height;
      const border = Number.parseFloat(window.getComputedStyle(panel).borderBottomWidth) || 0;
      const minHeight = summaryHeight + border;
      const fromHeight = Math.max(minHeight, currentHeight);
      const toHeight = opening ? minHeight + contentHeight : minHeight;
      panel.style.overflow = 'hidden';

      const height = panel.animate(
        [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
        {
          duration: opening ? 320 : 260,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both'
        }
      );

      const fade = content.animate(
        [
          {
            opacity: opening && !previous ? 0 : currentOpacity,
            transform: previous ? currentTransform : opening ? 'translateY(8px)' : 'translateY(0)'
          },
          {
            opacity: opening ? 1 : 0,
            transform: opening ? 'translateY(0)' : 'translateY(-5px)'
          }
        ],
        {
          duration: opening ? 280 : 210,
          easing: 'ease-out',
          fill: 'both'
        }
      );

      const state = { height, fade, opening };
      animations.set(panel, state);

      height.addEventListener('finish', () => {
        if (animations.get(panel) !== state) return;
        if (!opening) panel.open = false;
        fade.cancel();
        height.cancel();
        panel.style.removeProperty('overflow');
        animations.delete(panel);
      }, { once: true });
    }

    group.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      if (!summary || summary.parentElement?.parentElement !== group) return;
      const panel = summary.parentElement;
      if (!panels.includes(panel)) return;

      event.preventDefault();
      const previous = animations.get(panel);
      const opening = previous ? !previous.opening : !panel.open;

      if (opening) {
        panels.forEach((other) => {
          if (other === panel) return;
          const otherState = animations.get(other);
          if (otherState ? otherState.opening : other.open) transition(other, false);
        });
      }

      transition(panel, opening);
    });
  }

  function initRoot(root) {
    if (
      !root ||
      root.dataset
        .viceExperienceReady ===
        "true"
    ) {
      return;
    }

    root.dataset
      .viceExperienceReady =
      "true";

    initReveal(
      root
    );

    initAccordion(root);

    initMobileBuy(
      root
    );
  }

  document.addEventListener(
    "click",
    handleComboClick
  );

  document.addEventListener(
    "click",
    handleReviewClick
  );

  function initAll() {
    document
      .querySelectorAll(
        EXPERIENCE_SELECTOR
      )
      .forEach(
        initRoot
      );
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initAll,
      {
        once: true
      }
    );
  } else {
    initAll();
  }

  document.addEventListener(
    "shopify:section:load",
    initAll
  );
})();
